/**
 * Token storage + JWT claim decoding helpers.
 *
 * Tokens live in localStorage under two keys so refresh + access can be rotated
 * independently. The access token is short-lived; the refresh token is rotated
 * on every successful refresh by the backend.
 *
 * Impersonation tokens live in sessionStorage instead of localStorage so they
 * scope to a single browser tab. Opening "View as customer" pops a new tab
 * with the customer's token in sessionStorage; the original tab keeps the
 * reseller's localStorage tokens untouched. Closing the tab ends the
 * impersonation. There's no refresh for impersonation — when the 15-min
 * token expires the user re-launches "View as".
 *
 * `decodeJwt` is a parser, NOT a verifier. The API is the only authority that
 * verifies signatures. We use the claims here purely for the UI (show the
 * user's name in the Topbar, derive the active tenant for the sidebar, etc.).
 */

const ACCESS_TOKEN_KEY = 'ezsender:access_token';
const REFRESH_TOKEN_KEY = 'ezsender:refresh_token';
const IMPERSONATION_TOKEN_KEY = 'ezsender:impersonation_token';

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function safeSession(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Returns the impersonation token if this tab is in "view as" mode, else
 * the regular access token from localStorage. Callers don't need to know
 * which one is active — `api.ts` just attaches whatever this returns.
 */
export function getAccessToken(): string | null {
  const imp = safeSession()?.getItem(IMPERSONATION_TOKEN_KEY) ?? null;
  if (imp) return imp;
  return safeStorage()?.getItem(ACCESS_TOKEN_KEY) ?? null;
}

export function getRefreshToken(): string | null {
  // Impersonation has no refresh token — token expires, tab dies.
  if (isImpersonationActive()) return null;
  return safeStorage()?.getItem(REFRESH_TOKEN_KEY) ?? null;
}

export function setTokens(accessToken: string, refreshToken: string): void {
  const storage = safeStorage();
  if (!storage) return;
  storage.setItem(ACCESS_TOKEN_KEY, accessToken);
  storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  const storage = safeStorage();
  if (!storage) return;
  storage.removeItem(ACCESS_TOKEN_KEY);
  storage.removeItem(REFRESH_TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// Impersonation (sessionStorage — per-tab)
// ---------------------------------------------------------------------------

/**
 * Stores the impersonation token in sessionStorage. Only the tab that
 * received the handoff sees it; other tabs keep their reseller session.
 */
export function setImpersonationToken(token: string): void {
  safeSession()?.setItem(IMPERSONATION_TOKEN_KEY, token);
}

export function clearImpersonationToken(): void {
  safeSession()?.removeItem(IMPERSONATION_TOKEN_KEY);
}

export function isImpersonationActive(): boolean {
  return Boolean(safeSession()?.getItem(IMPERSONATION_TOKEN_KEY));
}

/**
 * Subset of JWT claims emitted by the EzSender API. The shape is dictated by
 * the backend's auth handler — keep in sync with `internal/services/auth`.
 *
 * `uid` and `tid` are int64 server-side and serialize as JSON numbers.
 * `typ` distinguishes access vs refresh tokens so the frontend can warn when
 * the wrong one is used.
 */
export interface JwtClaims {
  uid: number;
  tid: number;
  /** platform | reseller | customer — gates admin sidebar links + endpoints. */
  ttp?: 'platform' | 'reseller' | 'customer';
  role: string;
  email: string;
  typ: 'access' | 'refresh';
  exp: number;
  iat?: number;
  /** True when this token was minted by a reseller's "view as" action. */
  imp?: boolean;
  /** Acting user (reseller admin) when imp=true — for audit + UI banner. */
  act_uid?: number;
  act_email?: string;
}

/**
 * base64url decode for the JWT payload segment. Browser-only — atob is
 * always available; we don't need a Node.js Buffer fallback because this
 * code runs in the Vite-bundled SPA, not in any SSR context.
 */
function base64UrlDecode(segment: string): string {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  const base64 = normalized + padding;
  return decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
}

/**
 * Splits a JWT and returns its payload. Returns null if the token is malformed
 * or the payload is not the expected shape — never throws so callers can use
 * `decodeJwt(token) ?? fallback` safely.
 */
export function decodeJwt(token: string | null | undefined): JwtClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payloadSegment = parts[1];
  if (!payloadSegment) return null;
  try {
    const json = base64UrlDecode(payloadSegment);
    const parsed = JSON.parse(json) as Partial<JwtClaims>;
    if (
      typeof parsed.uid !== 'number' ||
      typeof parsed.tid !== 'number' ||
      typeof parsed.role !== 'string' ||
      typeof parsed.email !== 'string' ||
      typeof parsed.exp !== 'number' ||
      (parsed.typ !== 'access' && parsed.typ !== 'refresh')
    ) {
      return null;
    }
    return {
      uid: parsed.uid,
      tid: parsed.tid,
      ttp: parsed.ttp,
      role: parsed.role,
      email: parsed.email,
      typ: parsed.typ,
      exp: parsed.exp,
      iat: typeof parsed.iat === 'number' ? parsed.iat : undefined,
      imp: parsed.imp === true || undefined,
      act_uid: typeof parsed.act_uid === 'number' ? parsed.act_uid : undefined,
      act_email: typeof parsed.act_email === 'string' ? parsed.act_email : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * True if the access token is missing OR has expired (with a small safety
 * margin so we don't fire requests that will 401 by the time they hit the API).
 */
export function isAccessTokenExpired(token: string | null | undefined, skewSeconds = 30): boolean {
  const claims = decodeJwt(token);
  if (!claims) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return claims.exp - skewSeconds <= nowSeconds;
}
